import React, {useMemo} from 'react';
import {Table} from '@/web-components';
import TotalsLoader, {totalsLoaderQuery} from './TotalsLoader';
import ProjectPortfolioPeriodRow from './ProjectPortfolioPeriodRow';
import ProjectPortfolioPeriodProjectRows from './ProjectPortfolioPeriodProjectRows';
import ProjectPortfolioPeriodProgramRows from './ProjectPortfolioPeriodProgramRows';
import {filtersToSearchQuery} from '../ProjectPortfolioReportUtils';
import {ProjectPortfolioReportHeadersTooltips as HeadersTooltips} from '../ProjectPortfolioReportHeadersTooltips.tsx';
import {LoadMore} from '../../../loaders/LoadMore';
import {remapAndFormatFinancialMessage} from '../../../../forecast-app/shared/util/FinancialInternationalisationUtil';
import {GROUP_BY} from '../ProjectPortfolioReport';
import ProjectPortfolioPeriodConnectedProjectRows from './ProjectPortfolioPeriodConnectedProjectRows';
import ProjectPortfolioPeriodClientRows from './ProjectPortfolioPeriodClientRows';
import CompanySetupUtil from '../../../../forecast-app/shared/util/CompanySetupUtil';

export const ProjectPortfolioPeriodTable = ({
	theEyeOptionsNestedTree,
	filters,
	selectedDateRange: {startDate, endDate},
	setCsvDownloadFunction,
	hasBaseline,
	groupBy,
	useWinProbability,
}) => {
	const formatMessage = remapAndFormatFinancialMessage;
	const pageSize = 100;

	const boolEyeOptions = useMemo(() => {
		const bools = {};
		theEyeOptionsNestedTree.forEach(eyeOption => {
			bools[eyeOption.name] = eyeOption.checked;
			if (eyeOption.nestedOptions) {
				eyeOption.nestedOptions.forEach(nestedOption => {
					bools[nestedOption.name] = nestedOption.checked;
					if (nestedOption.nestedOptions) {
						nestedOption.nestedOptions.forEach(subNestedOption => {
							bools[subNestedOption.name] = subNestedOption.checked;
						});
					}
				});
			}
		});
		return bools;
	}, [theEyeOptionsNestedTree]);

	const numSelectedNestedOptions = useMemo(() => {
		const numSelectedNestedOpts = {};
		theEyeOptionsNestedTree.forEach(eyeOption => {
			numSelectedNestedOpts[eyeOption.name] = 0;
			if (eyeOption.nestedOptions) {
				eyeOption.nestedOptions.forEach(nestedOption => {
					numSelectedNestedOpts[eyeOption.name] += nestedOption.checked ? 1 : 0;
					numSelectedNestedOpts[nestedOption.name] = 0;
					if (nestedOption.nestedOptions) {
						nestedOption.nestedOptions.forEach(subNestedOption => {
							numSelectedNestedOpts[nestedOption.name] += subNestedOption.checked ? 1 : 0;
						});
					}
				});
			}
		});
		return numSelectedNestedOpts;
	}, [theEyeOptionsNestedTree]);

	return (
		<Table paddedCells>
			<Table.Header>
				<Table.HeaderColumn flex={1} visible width="EXTRA_LARGE" />
				{/* Project Overview */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_budget.project_overview'})}>
					<Table.HeaderColumn visible={boolEyeOptions.priorityLevel} usePadding align="left" width="MEDIUM">
						<HeadersTooltips.ProjectOverviewPriorityLevelTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_settings.priority_level'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectOverviewPriorityLevelTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.winProbability} usePadding align="right" width="SMALL">
						<HeadersTooltips.ProjectOverviewWinProbabilityTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_portfolio_report.win_prob'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectOverviewWinProbabilityTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.dates} usePadding align="left" width="SMALL">
						<HeadersTooltips.ProjectOverviewProjectStartDateTooltip size={'m'}>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.start'})}</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectOverviewProjectStartDateTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.dates} usePadding align="left" width="SMALL">
						<HeadersTooltips.ProjectOverviewProjectEndDateTooltip size={'m'}>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.end'})}</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectOverviewProjectEndDateTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.client} usePadding align="left" width="MEDIUM">
						<HeadersTooltips.ProjectOverviewClientTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'insights.component.list.column.client'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectOverviewClientTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.status} usePadding align="center" width="EXTRA_SMALL">
						<HeadersTooltips.ProjectOverviewStatusTooltip size={'m'}>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.status'})}</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectOverviewStatusTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.statusDescription} usePadding align="left" width="SUPER_LARGE">
						<HeadersTooltips.ProjectOverviewStatusDescriptionTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'common.statusDescription'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectOverviewStatusDescriptionTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.projectOwner} usePadding align="left" width="MEDIUM">
						<HeadersTooltips.ProjectOverviewProjectOwnerTooltip size={'m'}>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.project_owner'})}</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectOverviewProjectOwnerTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.stage} usePadding align="left" width="MEDIUM">
						<HeadersTooltips.ProjectOverviewStageTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'insights.component.list.column.status'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectOverviewStageTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.budgetType} usePadding align="left" width="MEDIUM">
						<HeadersTooltips.ProjectOverviewBudgetTypeTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'new_project_modal.budget_type'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectOverviewBudgetTypeTooltip>
					</Table.HeaderColumn>
					{CompanySetupUtil.hasFinance() ? (
						<Table.HeaderColumn visible={boolEyeOptions.rateCard} usePadding align="left" width={'LARGE'}>
							<HeadersTooltips.ProjectOverviewRateCardTooltip size={'m'}>
								<Table.HeaderColumn.Title>
									{formatMessage({id: 'insights.component.list.column.rateCard'})}
								</Table.HeaderColumn.Title>
							</HeadersTooltips.ProjectOverviewRateCardTooltip>
						</Table.HeaderColumn>
					) : (
						<Table.HeaderColumn visible={boolEyeOptions.rateCard} usePadding align="right" width={'MEDIUM'}>
							<HeadersTooltips.ProjectOverviewRateCardTooltip size={'m'}>
								<Table.HeaderColumn.Title>{formatMessage({id: 'common.rate'})}</Table.HeaderColumn.Title>
							</HeadersTooltips.ProjectOverviewRateCardTooltip>
						</Table.HeaderColumn>
					)}
					<Table.HeaderColumn visible={boolEyeOptions.progress} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ProjectOverviewProjectProgressTooltip size={'xl'}>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.progress'})}</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectOverviewProjectProgressTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* Project Hours */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_portfolio_report.project_hours'})}>
					<Table.HeaderColumn visible={boolEyeOptions.baselineHours} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ProjectHoursBaselineHoursTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id: 'baseline.hours',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectHoursBaselineHoursTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.approvedScope} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ProjectHoursEstimatedHoursTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id: 'common.estimated_hours',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectHoursEstimatedHoursTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.registeredHours} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ProjectHoursActualHoursTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id: 'common.actual_hours',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectHoursActualHoursTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.remainingHours} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ProjectHoursRemainingWorkHoursTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id: 'common.remaining_work_hours',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectHoursRemainingWorkHoursTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.forecastedHours} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ProjectHoursProjectedTotalHoursTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id: 'common.projected_total_hours',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectHoursProjectedTotalHoursTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* Baseline */}
				<Table.HeaderGrouping
					name={numSelectedNestedOptions.baseline < 2 ? '' : formatMessage({id: 'project_section.baseline'})}
				>
					<Table.HeaderColumn visible={boolEyeOptions.baselineRevenue} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.BaselineRevenueTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.baseline < 2
											? 'project_budget.baseline_revenue'
											: 'baseline.revenue',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.BaselineRevenueTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.baselineValueOfService} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.BaselineBillableValueOfServiceTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id: 'common.billable_value_of_service',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.BaselineBillableValueOfServiceTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.baselineCost} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.BaselineCostTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id: numSelectedNestedOptions.baseline < 2 ? 'project_budget.baseline_cost' : 'common.cost',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.BaselineCostTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.baselineProfit} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.BaselineProfitTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.baseline < 2
											? 'project_budget.baseline_profit'
											: 'common.profit',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.BaselineProfitTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.baselineMargin} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.BaselineMarginTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.baseline < 2
											? 'project_budget.baseline_margin'
											: 'common.margin',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.BaselineMarginTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* Plan / Estimated */}
				<Table.HeaderGrouping name={numSelectedNestedOptions.planned < 2 ? '' : formatMessage({id: 'common.planned'})}>
					<Table.HeaderColumn visible={boolEyeOptions.estimatedRevenue} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.EstimatedRevenueTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.planned < 2
											? 'common.estimated_revenue'
											: 'project_budget.revenue',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.EstimatedRevenueTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.planRevenue} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.EstimatedBillableValueOfServiceTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.planned < 2
											? 'project_budget.planned_billable_time_and_expenses'
											: 'project_budget.billable_time_and_expenses',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.EstimatedBillableValueOfServiceTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.planCost} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.EstimatedCostTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id: numSelectedNestedOptions.planned < 2 ? 'project_budget.planned_cost' : 'common.cost',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.EstimatedCostTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.planProfit} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.EstimatedProfitTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.planned < 2
											? 'project_budget.planned_profit'
											: 'common.profit',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.EstimatedProfitTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.planMargin} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.EstimatedMarginTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.planned < 2
											? 'project_budget.planned_margin'
											: 'common.margin',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.EstimatedMarginTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* Actuals */}
				<Table.HeaderGrouping
					name={numSelectedNestedOptions.actual < 2 ? '' : formatMessage({id: 'project_budget.actuals'})}
				>
					<Table.HeaderColumn visible={boolEyeOptions.actualRevenue} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ActualRevenueTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.actual < 2
											? 'common.actual_revenue'
											: 'project_budget.revenue',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ActualRevenueTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.billableActualTimeAndExpenses}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.ActualBillableValueOfServiceTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.actual < 2
											? 'project_budget.actual_billable_time_and_expenses'
											: 'project_budget.billable_time_and_expenses',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ActualBillableValueOfServiceTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.actualCost} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ActualCostTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id: numSelectedNestedOptions.actual < 2 ? 'common.actual_cost' : 'common.cost',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ActualCostTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.actualProfit} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ActualProfitTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id: numSelectedNestedOptions.actual < 2 ? 'project_budget.actual_profit' : 'common.profit',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ActualProfitTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.actualMargin} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ActualMarginTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id: numSelectedNestedOptions.actual < 2 ? 'project_budget.actual_margin' : 'common.margin',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ActualMarginTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* Forecast to Complete / Remaining Work */}
				<Table.HeaderGrouping
					name={
						numSelectedNestedOptions.remaining < 2
							? ''
							: formatMessage({
									id: 'project_budget.forecast_to_complete',
							  })
					}
				>
					<Table.HeaderColumn visible={boolEyeOptions.remainingWorkRevenue} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.RemainingWorkRevenueTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.remaining < 2
											? 'project_budget.remaining_work_revenue'
											: 'project_budget.revenue',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.RemainingWorkRevenueTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.billableForecastTimeAndExpensesToComplete}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.RemainingWorkBillableValueOfServiceTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.remaining < 2
											? 'project_budget.forecast_billable_time'
											: 'project_budget.billable_time_and_expenses',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.RemainingWorkBillableValueOfServiceTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.forecastCostToComplete} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.RemainingWorkCostTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.remaining < 2
											? 'project_budget.forecast_cost_to_complete'
											: 'common.cost',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.RemainingWorkCostTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.forecastProfitToComplete}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.RemainingWorkProfitTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.remaining < 2
											? 'project_budget.forecast_profit_to_complete'
											: 'common.profit',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.RemainingWorkProfitTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.forecastMarginToComplete}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.RemainingWorkMarginTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.remaining < 2
											? 'project_budget.forecast_margin_to_complete'
											: 'common.margin',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.RemainingWorkMarginTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* Forecasted / Projected Total */}
				<Table.HeaderGrouping
					name={
						numSelectedNestedOptions.forecasted < 2
							? ''
							: formatMessage({
									id: 'project_budget.total_at_completion',
							  })
					}
				>
					<Table.HeaderColumn visible={boolEyeOptions.projectTotalRevenue} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ProjectedTotalRevenueTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.forecasted < 2
											? 'common.projected_total_revenue'
											: 'project_budget.revenue',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectedTotalRevenueTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.forecastedRevenue} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ProjectedTotalBillableValueOfServiceTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.forecasted < 2
											? 'common.projected_total_billable_value_of_service'
											: 'project_budget.billable_time_and_expenses',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectedTotalBillableValueOfServiceTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.forecastedCost} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ProjectedTotalCostTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id: numSelectedNestedOptions.forecasted < 2 ? 'common.projected_total_cost' : 'common.cost',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectedTotalCostTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.forecastedProfit} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ProjectedTotalProfitTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.forecasted < 2
											? 'common.projected_total_profit'
											: 'common.profit',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectedTotalProfitTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.forecastedMargin} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.ProjectedTotalMarginTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({
									id:
										numSelectedNestedOptions.forecasted < 2
											? 'common.projected_total_margin'
											: 'common.margin',
								})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.ProjectedTotalMarginTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* Invoiced */}
				<Table.HeaderGrouping name={numSelectedNestedOptions.invoice < 2 ? '' : formatMessage({id: 'common.invoiced'})}>
					<Table.HeaderColumn visible={boolEyeOptions.invoiced} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.InvoicedInvoicedTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_portfolio_report.invoiced'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.InvoicedInvoicedTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.paid} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.InvoicedInvoicePaidTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_portfolio_report.invoiced_paid'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.InvoicedInvoicePaidTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* Revenue Recognition */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_budget.revenue_recognition'})}>
					<Table.HeaderColumn
						visible={boolEyeOptions.recognitionLockedRevenue}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.RevenueRecognitionFromLockedMonthsTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_budget.from_locked_months'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.RevenueRecognitionFromLockedMonthsTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.recognitionOpenRevenue} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.RevenueRecognitionFromOpenMonthsTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_budget.from_open_months'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.RevenueRecognitionFromOpenMonthsTooltip>
					</Table.HeaderColumn>

					<Table.HeaderColumn visible={boolEyeOptions.recognitionAmount} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.total'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* Fixed Price */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_budget.fixed_price'})}>
					<Table.HeaderColumn visible={boolEyeOptions.fixedPriceRevenue} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.FixedPriceTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_budget.fixed_price'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.FixedPriceTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* (Plan / Estimated) vs. Actual */}
				<Table.HeaderGrouping
					name={numSelectedNestedOptions.planActual < 2 ? '' : formatMessage({id: 'project_budget.plan_vs_actual'})}
				>
					<Table.HeaderColumn visible={boolEyeOptions.planActualRevenue} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.EstimatedVsActualRevenueTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{numSelectedNestedOptions.planActual < 2
									? formatMessage({id: 'project_budget.plan_vs_actual'}) + ' '
									: ''}
								{formatMessage({id: 'project_budget.revenue'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.EstimatedVsActualRevenueTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.planActualCost} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.EstimatedVsActualCostTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{numSelectedNestedOptions.planActual < 2
									? formatMessage({id: 'project_budget.plan_vs_actual'}) + ' '
									: ''}
								{formatMessage({id: 'common.cost'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.EstimatedVsActualCostTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.planActualProfit} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.EstimatedVsActualProfitTooltip size={'m'}>
							<Table.HeaderColumn.Title>
								{numSelectedNestedOptions.planActual < 2
									? formatMessage({id: 'project_budget.plan_vs_actual'}) + ' '
									: ''}
								{formatMessage({id: 'common.profit'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.EstimatedVsActualProfitTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.planBillableActualTimeAndExpenses}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.EstimatedVsActualBillableValueOfServiceTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{numSelectedNestedOptions.planActual < 2
									? formatMessage({id: 'project_budget.plan_vs_actual'}) + ' '
									: ''}
								{formatMessage({id: 'common.billable_value_of_service'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.EstimatedVsActualBillableValueOfServiceTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* (Plan / Estimated) vs. Forecasted */}
				<Table.HeaderGrouping
					name={
						numSelectedNestedOptions.planForecasted < 2
							? ''
							: formatMessage({id: 'project_budget.plan_vs_forecast'})
					}
				>
					<Table.HeaderColumn visible={boolEyeOptions.planForecastedRevenue} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.EstimatedVsProjectedTotalRevenueTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{numSelectedNestedOptions.planForecasted < 2
									? formatMessage({id: 'project_budget.plan_vs_forecast'}) + ' '
									: ''}
								{formatMessage({id: 'project_budget.revenue'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.EstimatedVsProjectedTotalRevenueTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.planForecastedCost} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.EstimatedVsProjectedTotalCostTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{numSelectedNestedOptions.planForecasted < 2
									? formatMessage({id: 'project_budget.plan_vs_forecast'}) + ' '
									: ''}
								{formatMessage({id: 'common.cost'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.EstimatedVsProjectedTotalCostTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.planForecastedProfit} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.EstimatedVsProjectedTotalProfitTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{numSelectedNestedOptions.planForecasted < 2
									? formatMessage({id: 'project_budget.plan_vs_forecast'}) + ' '
									: ''}
								{formatMessage({id: 'common.profit'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.EstimatedVsProjectedTotalProfitTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.planForecastedBillableTimeAndExpenses}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.EstimatedVsProjectedTotalBillableValueOfServiceTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{numSelectedNestedOptions.planForecasted < 2
									? formatMessage({id: 'project_budget.plan_vs_forecast'}) + ' '
									: ''}
								{formatMessage({id: 'common.billable_value_of_service'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.EstimatedVsProjectedTotalBillableValueOfServiceTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* Over/Under Servicing */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_portfolio_report.over_under_servicing'})}>
					<Table.HeaderColumn visible={boolEyeOptions.projectedTotalRevenue} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.OverUnderServicingProjectedTotalRevenueTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'common.projected_total_revenue'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.OverUnderServicingProjectedTotalRevenueTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.projectedTotalBillableValueOfService}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.OverUnderServicingProjectedTotalBillableValueOfServiceTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'common.projected_total_billable_value_of_service'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.OverUnderServicingProjectedTotalBillableValueOfServiceTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.projectedTotalNonBillableValueOfService}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.OverUnderServicingProjectedTotalNonBillableValueOfServiceTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'common.projected_total_non_billable_value_of_service'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.OverUnderServicingProjectedTotalNonBillableValueOfServiceTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.projectedTotalValueOfService}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.OverUnderServicingProjectedTotalValueOfServiceTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'common.projected_total_value_of_service'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.OverUnderServicingProjectedTotalValueOfServiceTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.projectedBudgetRemaining}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.OverUnderServicingProjectedRemainingBudgetTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'common.projected_budget_remaining'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.OverUnderServicingProjectedRemainingBudgetTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.projectedOverUnderServicing}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.OverUnderServicingProjectedOverUnderServicingTooltip size={'xl'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_portfolio_report.projected_over_under_servicing'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.OverUnderServicingProjectedOverUnderServicingTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* (Invoiced vs. Actual) / (Accrued / Deferred) */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_budget.accrued'})}>
					<Table.HeaderColumn visible={boolEyeOptions.invoicedActualInvoiced} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.AccruedDeferredInvoicedTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_budget.invoiced'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.AccruedDeferredInvoicedTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.invoicedBillableActualTimeAndExpenses}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'project_budget.actual_billable_time_and_expenses'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.invoicedRecognitionLockedRevenue}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.AccruedDeferredRevenueRecognitionFromLockedMonthsTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_budget.revenue_recognition_from_locked_months'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.AccruedDeferredRevenueRecognitionFromLockedMonthsTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.accruedDeferred} usePadding align="right" width="MEDIUM">
						<HeadersTooltips.AccruedDeferredAccruedDeferredTooltip size={'l'}>
							<Table.HeaderColumn.Title>{formatMessage({id: 'project_budget.accrued'})}</Table.HeaderColumn.Title>
						</HeadersTooltips.AccruedDeferredAccruedDeferredTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* Labels */}
				<Table.HeaderGrouping name={formatMessage({id: 'common.labels'})}>
					<Table.HeaderColumn visible={boolEyeOptions.labels} usePadding align="center" width="EXTRA_LARGE">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.labels'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				{/* Retainer period target */}
				<Table.HeaderGrouping
					name={
						numSelectedNestedOptions.retainerPeriodTarget < 2
							? ''
							: formatMessage({id: 'project_budget.retainer_period_target'})
					}
				>
					<Table.HeaderColumn
						visible={boolEyeOptions.retainerPeriodTargetMinutes}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.RetainerPeriodTargetTimeTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{numSelectedNestedOptions.retainerPeriodTarget < 2
									? formatMessage({
											id: 'project_budget.retainer_period_target',
									  }) + ' '
									: ''}
								{formatMessage({id: 'common.time'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.RetainerPeriodTargetTimeTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.retainerPeriodTargetPrice}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<HeadersTooltips.RetainerPeriodTargetPriceTooltip size={'l'}>
							<Table.HeaderColumn.Title>
								{numSelectedNestedOptions.retainerPeriodTarget < 2
									? formatMessage({
											id: 'project_budget.retainer_period_target',
									  }) + ' '
									: ''}
								{formatMessage({id: 'project_budget.retainer_period_target_price'})}
							</Table.HeaderColumn.Title>
						</HeadersTooltips.RetainerPeriodTargetPriceTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
			</Table.Header>

			<LoadMore
				key="query-render-totals"
				query={totalsLoaderQuery}
				variables={{
					searchQuery: filtersToSearchQuery(filters),
					startYear: startDate ? startDate.year() : undefined,
					startMonth: startDate ? startDate.month() + 1 : undefined,
					startDay: startDate ? startDate.date() : undefined,
					endYear: endDate ? endDate.year() : undefined,
					endMonth: endDate ? endDate.month() + 1 : undefined,
					endDay: endDate ? endDate.date() : undefined,
					ignoreBaselineWinChance: !useWinProbability,
					excludeOpportunityActuals: true,
				}}
				loader={<TotalsLoader />}
			>
				{({data}) => {
					return (
						<Table.Rows data={data} canExpand={false} hasHeader>
							{({rowData, tableColumnsProps}) => {
								return (
									<ProjectPortfolioPeriodRow
										financialNumbers={rowData.financialNumbers}
										rowData={rowData}
										tableColumnProps={tableColumnsProps}
										headerColumn={<Table.Column>Totals</Table.Column>}
										useWinProbability={useWinProbability}
									/>
								);
							}}
						</Table.Rows>
					);
				}}
			</LoadMore>
			{groupBy === GROUP_BY.PROGRAM ? (
				<ProjectPortfolioPeriodProgramRows
					setCsvDownloadFunction={setCsvDownloadFunction}
					hasBaseline={hasBaseline}
					pageSize={pageSize}
					startDate={startDate}
					endDate={endDate}
					filters={filters}
					boolEyeOptions={boolEyeOptions}
					useWinProbability={useWinProbability}
				></ProjectPortfolioPeriodProgramRows>
			) : groupBy === GROUP_BY.CONNECTED_PROJECT ? (
				<ProjectPortfolioPeriodConnectedProjectRows
					setCsvDownloadFunction={setCsvDownloadFunction}
					hasBaseline={hasBaseline}
					pageSize={pageSize}
					startDate={startDate}
					endDate={endDate}
					filters={filters}
					boolEyeOptions={boolEyeOptions}
					useWinProbability={useWinProbability}
				></ProjectPortfolioPeriodConnectedProjectRows>
			) : groupBy === GROUP_BY.CLIENT ? (
				<ProjectPortfolioPeriodClientRows
					setCsvDownloadFunction={setCsvDownloadFunction}
					hasBaseline={hasBaseline}
					pageSize={pageSize}
					startDate={startDate}
					endDate={endDate}
					filters={filters}
					boolEyeOptions={boolEyeOptions}
					useWinProbability={useWinProbability}
				></ProjectPortfolioPeriodClientRows>
			) : (
				<ProjectPortfolioPeriodProjectRows
					setCsvDownloadFunction={setCsvDownloadFunction}
					hasBaseline={hasBaseline}
					pageSize={pageSize}
					startDate={startDate}
					endDate={endDate}
					filters={filters}
					boolEyeOptions={boolEyeOptions}
					useWinProbability={useWinProbability}
				></ProjectPortfolioPeriodProjectRows>
			)}
		</Table>
	);
};
